<template>
    <div v-if="positions">
        <div class="flex justify-center text-sm">
            <div class="max-w-full overflow-x-auto mb-5">
                <ul class="steps mb-5 pt-10">
                    <li
                        v-for="(position, index) in CPositions"
                        :key="index"
                        class="flex items-center flex-col w-[430px] justify-start step relative"
                        :class="{ 'step-primary': position.tasks?.length, 'step-disabled': checkIndexDisabled(index) }">
                        <div
                            v-if="index !== positions.length - 1"
                            class="absolute top-[-30px] right-0"
                            style="transform: translateX(50%)">
                            <div v-if="typeof position?.travelTimeToNextPosition?.duration === 'number'">
                                <h3
                                    v-if="checkIndexDisabled(index) || !editTravelTime"
                                    :class="{ 'text-error-500': !checkIndexDisabled(index) }">
                                    {{ $formatters.convertMinutesToNaturalLanguage(position?.travelTimeToNextPosition?.duration || 0, 'minute') }}
                                </h3>
                                <edit-travel-time
                                    v-else
                                    :value="position.travelTimeToNextPosition.duration"
                                    type="minute"
                                    @update="(val: number) => positions[index].travelTimeToNextPosition.duration = val * 60" />
                            </div>
                        </div>
                        <div class="w-[420px] mb-2 h-full">
                            <p class="text-lg font-bold text-default-500">
                                {{ position.nodeLocationName }}
                            </p>

                            <hts-step-by-step
                                :add-end-point="true"
                                class="mt-20"
                                :class="{ 'step-disabled': !checkIndexDisabled(index) }"
                                :steps="position.tasks">
                                <template #feltInStep="step: any">
                                    <div class="flex flex-row">
                                        <div class="flex items-start">
                                            <div>
                                                <h5 class="text-left break-words max-w-[190px]">
                                                    <span
                                                        v-if="step.step.type === 'waiting'"
                                                        :class="{ 'text-error-500': !checkIndexDisabled(index) }">
                                                        {{ $t('labels.waiting') }}
                                                    </span>
                                                    <span v-else>
                                                        {{ step.step.taskName }}
                                                    </span>
                                                </h5>
                                                <p
                                                    v-if="step.step.type !== 'waiting'"
                                                    class="text-left break-words max-w-[190px] italic">
                                                    {{ step.step?.businessName || '' }}
                                                </p>
                                                <div class="max-w-[170px]">
                                                    <div
                                                        v-if="['HEADWAY_DRIVER', 'CUSTOMER'].includes(step.step.responsibleForImplementation)"
                                                        class="text-left flex flex-col gap-2 pl-1 w-full bg-grey-200">
                                                        <span>
                                                            {{ $t('labels.performedBy') }}
                                                        </span>
                                                        <div class="border py-4 rounded-lg pl-1 bg-gray-200">
                                                            <span>
                                                                {{ labelResponsibleForImplementation(step.step.responsibleForImplementation) }}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <hts-form-select
                                                        v-else
                                                        v-model="step.step.executor"
                                                        :label="$t('labels.performedBy')"
                                                        :disabled="checkIndexDisabled(index) || ['HEADWAY_DRIVER', 'CUSTOMER'].includes(step.step.responsibleForImplementation) || disabled"
                                                        required
                                                        :items="itemSelectExecutor"
                                                        key-id="value"
                                                        key-label="label"
                                                        @change="(val) => updatePerformedBy(step.step, val)" />
                                                </div>
                                            </div>
                                            <div v-if="getDataCondition(position.index, step.step.task).isHave && step.step.type !== 'waiting'">
                                                <hts-tooltip
                                                    class="mt-1"
                                                    placement="top"
                                                    :offset="[-20, 20]">
                                                    <div>
                                                        <div v-if="getDataCondition(position.index, step.step.task).pre.length">
                                                            <h5>
                                                                Công việc cần thực hiện trước:
                                                            </h5>
                                                            <p
                                                                v-for="pre in getDataCondition(position.index, step.step.task).pre"
                                                                :key="pre">
                                                                {{ pre.taskConditionName }}
                                                            </p>
                                                        </div>
                                                        <div v-if="getDataCondition(position.index, step.step.task).post.length">
                                                            <h5>
                                                                Công việc cần thực hiện sau:
                                                            </h5>
                                                            <p
                                                                v-for="post in getDataCondition(position.index, step.step.task).post"
                                                                :key="post">
                                                                {{ post.taskConditionName }}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <template #reference>
                                                        <hi-cog-8-tooth-icon class="w-8 h-8 ml-2 text-info-500" />
                                                    </template>
                                                </hts-tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template #feltInLine="step: any">
                                    <div v-if="!checkIndexDisabled(index) && editTravelTime && step.step.type !== 'waiting'">
                                        <hts-form-input
                                            v-model="step.step.timeComplete"
                                            class="w-36"
                                            type="number">
                                            <template #suffix>
                                                giờ
                                            </template>
                                        </hts-form-input>
                                    </div>
                                    <p
                                        v-else
                                        class="text-lg"
                                        :class="{ 'text-error-500': !checkIndexDisabled(index) }">
                                        <span>
                                            {{ $formatters.convertMinutesToNaturalLanguage(Math.round(step.step.timeComplete * 60), 'minute') }}
                                        </span>
                                    </p>
                                </template>
                                <template #right="step">
                                    <div
                                        v-if="checkHasTimeConditionTypeInTask(step.step.timeConditions, 'START')"
                                        class="border-2 border-default-200 box-border p-2 border-dashed mt-1">
                                        <h5 class="text-left">
                                            {{ $t('labels.timeStart') }}
                                        </h5>
                                        <div>
                                            <div
                                                v-for="(timeCondition, indexTime) in step.step.timeConditions"
                                                :key="timeCondition">
                                                <div
                                                    v-if="timeCondition.type === 'START'"
                                                    class="w-[150px] min-w-[150px]">
                                                    <hts-tooltip>
                                                        <p>
                                                            Thời hạn
                                                            <span class="font-bold">
                                                                {{ gettimeTypeLabels(timeCondition?.timeType) }}
                                                            </span>
                                                            để <span class="font-bold">
                                                                bắt đầu
                                                            </span> tác vụ
                                                            <span class="font-bold">
                                                                {{ step.step.taskName }}
                                                            </span>
                                                            mà không phát sinh chi phí
                                                            <span class="font-bold">
                                                                {{ timeCondition.name }}
                                                            </span>
                                                        </p>
                                                        <template #reference>
                                                            <date-time
                                                                v-model="timeCondition.time"
                                                                :label="timeCondition.name"
                                                                class="w-[150px] min-w-[150px]"
                                                                required
                                                                :disabled="checkIndexDisabled(index) || disabled"
                                                                :color="getColors(timeCondition?.timeType, timeCondition?.isActual, checkIndexDisabled(index))" />
                                                        </template>
                                                    </hts-tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        v-if="
                                            checkHasTimeConditionTypeInTask(step.step.timeConditions, 'END') || checkHasTimeConditionTypeInTask(step.step.timeConditions, 'START')
                                        "
                                        style="min-height: 50px" />
                                    <div
                                        v-else
                                        style="min-height: 100px" />
                                    <div
                                        v-if="checkHasTimeConditionTypeInTask(step.step.timeConditions, 'END')"
                                        class="border-2 border-default-200 box-border p-2 border-dashed mb-1">
                                        <h5 class="text-left">
                                            {{ $t('labels.timeEnd') }}
                                        </h5>
                                        <div>
                                            <div
                                                v-for="(timeCondition, indexTime) in step.step.timeConditions"
                                                :key="timeCondition"
                                                class="flex">
                                                <div
                                                    v-if="timeCondition.type === 'END'"
                                                    class="w-[150px] min-w-[150px]">
                                                    <hts-tooltip>
                                                        <p>
                                                            Thời hạn
                                                            <span class="font-bold">
                                                                {{ gettimeTypeLabels(timeCondition?.timeType) }}
                                                            </span>
                                                            để <span class="font-bold">
                                                                kết thúc
                                                            </span> tác vụ
                                                            <span class="font-bold">
                                                                {{ step.step.taskName }}
                                                            </span>
                                                            mà không phát sinh chi phí
                                                            <span class="font-bold">
                                                                {{ timeCondition.name }}
                                                            </span>
                                                        </p>

                                                        <template #reference>
                                                            <date-time
                                                                v-model="timeCondition.time"
                                                                :label="timeCondition.name"
                                                                class="w-[150px] min-w-[150px]"
                                                                required
                                                                :disabled="checkIndexDisabled(index) || disabled"
                                                                :color="getColors(timeCondition?.timeType, timeCondition?.isActual, checkIndexDisabled(index))" />
                                                        </template>
                                                    </hts-tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </hts-step-by-step>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import DateTime from './component/date-time.vue'
import EditTravelTime from './component/edit-travel-time.vue'
const { $formatters } = useNuxtApp()
const store = $store()

const props = defineProps({
  positions: {
    type: Array,
    required: true
  },
  condition: {
    type: Array,
    required: true
  },
  waitingTasks: {
    type: Array,
    required: true
  },
  disabled: Boolean,
  editTravelTime: {
    type: Boolean
  },
  indexBegin: {
    type: Number as PropType<number>,
    default: 0
  },
  indexEnd: {
    type: Number as PropType<number>,
    default: 999
  }
})

const positions: Ref<Array<any>> = toRefs(props).positions
const condition: Ref<Array<any>> = toRefs(props).condition
const waitingTasks: Ref<Array<any>> = toRefs(props).waitingTasks

const CPositions = computed({
  get: () => {
    const positionDatas = $lodash.cloneDeep(positions?.value) || []
    const waitingTasksData = $lodash.cloneDeep(waitingTasks?.value) || []

    return positionDatas.map((position: any, index: number) => {
      const newPosition = $lodash.cloneDeep(position)

      let tasks = [] as any[]

      if (newPosition.travelTimeToNextPosition) {
        newPosition.travelTimeToNextPosition.duration = (newPosition?.travelTimeToNextPosition?.duration || 0) / 60
      }

      newPosition.tasks?.forEach((task: any, indexT: number) => {
        // change positions when update time
        tasks.push(positions?.value[index].tasks[indexT])

        const firstWaiting =
          waitingTasksData?.filter((item: any) => {
            return item.index === index + 1 && indexT === 0 && !item.task
          }) || []

        const taskWaiting =
          waitingTasksData?.filter((item: any) => {
            return item.index === index + 1 && item.task === task.task
          }) || []

        if (firstWaiting.length) {
          tasks = [...firstWaiting, ...tasks]
        }
        if (taskWaiting.length) {
          tasks = tasks.concat(taskWaiting)
        }
      })

      newPosition.tasks = tasks

      return newPosition
    })
  },
  set: (val) => {
    console.log('val', val)
  }
})

const getColors = (timeType: string, isActual: boolean = false, disabled: boolean = false) => {
  if (disabled) {
    return 'disabled'
  }
  if (isActual) {
    return 'default'
  }
  if (timeType === 'EARLIEST') {
    return 'success'
  }

  if (timeType === 'LATEST') {
    return 'warning'
  }

  return 'info'
}

const gettimeTypeLabels = (timeType: string) => {
  if (timeType === 'EARLIEST') {
    return String($t('labels.earliest')).toLowerCase()
  }

  if (timeType === 'LATEST') {
    return String($t('labels.latest')).toLowerCase()
  }

  return String($t('labels.permanent')).toLowerCase()
}

const checkHasTimeConditionTypeInTask = (timeConditions: any, type: string) => {
  if (Array.isArray(timeConditions)) {
    const results = timeConditions.map((timeCondition: any) => timeCondition.type === type)
    if (results.includes(true)) {
      return true
    }
  }
  return false
}

const getDataCondition = (indexPosition: number, taskId: string) => {
  const pre: any[] = []
  const post: any[] = []
  let isHave: boolean = false

  if (Array.isArray(condition?.value)) {
    for (const item of condition.value) {
      if (item.index === indexPosition && item.task === taskId) {
        if (item.condition === 'pre') {
          isHave = true
          pre.push(item)
        }
        if (item.condition === 'post') {
          isHave = true
          post.push(item)
        }
      }
    }
  }

  return {
    pre,
    post,
    isHave
  }
}
const checkIndexDisabled = (index: number) => {
  return index < props.indexBegin || index > props.indexEnd
}
const itemSelectExecutor = [
  {
    label: `${store.system.workspace.name} - ${$t('labels.abstemious')}`,
    value: 'MODERATOR'
  },
  {
    label: `${store.system.workspace.name} - ${$t('labels.customerSupport')}`,
    value: 'CUSTOMER_SUPPORT'
  }
]

const labelResponsibleForImplementation = (type: string) => {
  const workspaceName = store.system.workspace.name
  if (type === 'HEADWAY') {
    return workspaceName + ' - ' + $t('labels.other')
  }
  if (type === 'HEADWAY_DRIVER') {
    return workspaceName + ' - ' + $t('labels.driver')
  }
  if (type === 'CUSTOMER') {
    return $t('labels.customer')
  }
}

const updatePerformedBy = (task: any, val: any) => {
  if (task.type !== 'waiting') {
    return
  }

  // Kiểm tra tính hợp lệ của val và waitingTasks
  if (!val || val.length === 0 || !waitingTasks || !waitingTasks.value || waitingTasks.value.length === 0) {
    return
  }

  waitingTasks.value = waitingTasks.value.map((taskWaiting) => {
    const taskWaitingId = taskWaiting._id
    if (taskWaitingId && taskWaitingId === task._id) {
      taskWaiting.executor = val[0].value
    }
    return taskWaiting
  })
}
</script>
